import { useId, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  OutlinedInput,
  Stack,
  Typography,
  TextField,
} from "@mui/material";

export interface EvidenceReference {
  quote: string;
  path?: string | null;
  location?:
    | { kind: "raw"; start: number; end: number; revision: string }
    | { kind: "structured"; path: string; revision: string };
}
export interface EvidenceSource {
  rawText: string;
  entries: Array<EvidenceReference & { id: string }>;
}
export interface UnsupportedClaim {
  text: string;
  start: number;
  end: number;
}

// Only highlight spans that still refer to the exact answer under review.
export function answerHighlights(
  text: string,
  claims: UnsupportedClaim[] = [],
) {
  return claims.filter(
    (c) =>
      Number.isInteger(c.start) &&
      Number.isInteger(c.end) &&
      c.start >= 0 &&
      c.end > c.start &&
      text.slice(c.start, c.end) === c.text,
  );
}
const key = (text: string) =>
  text.replace(/[“”]/g, '"').replace(/[‘’]/g, "'").replace(/\s+/g, " ").trim();
function initialSelection(
  source: EvidenceSource,
  evidence: EvidenceReference[],
) {
  const ids = new Set<string>();
  for (const reference of evidence) {
    const matches = source.entries.filter((entry) =>
      reference.location
        ? JSON.stringify(reference.location) ===
            JSON.stringify(entry.location) ||
          (reference.location.kind === "raw" &&
            entry.location?.kind === "raw" &&
            reference.location.revision === entry.location.revision &&
            reference.location.start >= entry.location.start &&
            reference.location.end <= entry.location.end &&
            key(
              source.rawText.slice(
                reference.location.start,
                reference.location.end,
              ),
            ) === key(reference.quote))
        : (reference.path || null) === (entry.path || null) &&
          !!key(reference.quote) &&
          key(entry.quote).includes(key(reference.quote)),
    );
    // An unresolved repeated quote must be selected explicitly by the admin.
    if (matches.length === 1) ids.add(matches[0].id);
  }
  return [...ids];
}
export function highlightedText(
  text: string,
  spans: Array<{ start: number; end: number }>,
) {
  const parts = [];
  let offset = 0;
  for (const span of [...spans].sort((a, b) => a.start - b.start)) {
    if (span.start < offset || span.start < 0 || span.end > text.length)
      continue;
    parts.push(text.slice(offset, span.start));
    parts.push(
      <mark key={`${span.start}-${span.end}`}>
        {text.slice(span.start, span.end)}
      </mark>,
    );
    offset = span.end;
  }
  parts.push(text.slice(offset));
  return parts;
}
const PAGE_SIZE = 12;
const fieldLabel = (path: string) =>
  path
    .split(".")
    .map((part) =>
      /^\d+$/.test(part) ? `Item ${Number(part) + 1}` : part.replace(/_/g, " "),
    )
    .join(" › ");

export function SemanticEvidencePanel({
  source,
  answerText,
  evidence,
  claims,
  sourceKind,
  revision,
  busy,
  reviewError,
  onClose,
  onUse,
  reassessment = false,
  failureReason,
}: {
  source: EvidenceSource;
  answerText: string;
  evidence: EvidenceReference[];
  claims?: UnsupportedClaim[];
  sourceKind: string;
  revision: string;
  busy: boolean;
  reviewError?: string;
  onClose: () => void;
  onUse: (
    references: EvidenceReference[],
    explanation?: string,
  ) => Promise<boolean>;
  reassessment?: boolean;
  failureReason?: string;
}) {
  // Freeze the reviewed source while open. Polling cannot move passages or replace
  // selections; a changed revision blocks submission until the panel is reopened.
  const [opened] = useState(() => ({
    source,
    answerText,
    evidence,
    claims,
    failureReason,
    revision,
    initialIds: initialSelection(source, evidence),
  }));
  const stale = opened.revision !== revision;
  const [selected, setSelected] = useState(opened.initialIds);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [browseAll, setBrowseAll] = useState(false);
  const [showRaw, setShowRaw] = useState(false);
  const [error, setError] = useState("");
  const [explanation, setExplanation] = useState("");
  const submitting = useRef(false);
  const searchId = useId();
  const indexed = useMemo(() => {
    const initial = new Set(opened.initialIds);
    return [...opened.source.entries]
      .sort((a, b) => Number(initial.has(b.id)) - Number(initial.has(a.id)))
      .map((entry) => ({
        entry,
        searchText: key(`${entry.path || ""} ${entry.quote}`).toLowerCase(),
      }));
  }, [opened]);
  const query = key(search).toLowerCase();
  const matches = useMemo(() => {
    const suggested = new Set([...opened.initialIds, ...selected]);
    return indexed
      .filter((e) =>
        query
          ? e.searchText.includes(query)
          : browseAll || suggested.has(e.entry.id),
      )
      .map((e) => e.entry);
  }, [indexed, query, browseAll, opened, selected]);
  const pageCount = Math.max(1, Math.ceil(matches.length / PAGE_SIZE));
  const currentPage = Math.min(page, pageCount - 1);
  const visible = matches.slice(
    currentPage * PAGE_SIZE,
    (currentPage + 1) * PAGE_SIZE,
  );
  const selectedEntries = useMemo(() => {
    const ids = new Set(selected);
    return indexed.filter((e) => ids.has(e.entry.id)).map((e) => e.entry);
  }, [indexed, selected]);
  const sourceHighlights = useMemo(() => {
    if (!showRaw) return [];
    const ids = new Set(selected);
    const matchedIds = new Set(query ? matches.map((e) => e.id) : []);
    return opened.source.entries.flatMap((e) =>
      e.location?.kind === "raw" && (ids.has(e.id) || matchedIds.has(e.id))
        ? [{ start: e.location.start, end: e.location.end }]
        : [],
    );
  }, [showRaw, opened, selected, query, matches]);
  const toggle = (id: string, checked: boolean) =>
    setSelected((ids) =>
      checked
        ? ids.includes(id)
          ? ids
          : [...ids, id]
        : ids.filter((value) => value !== id),
    );
  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="evidence-panel-title"
    >
      <DialogTitle id="evidence-panel-title">
        {reassessment ? "Review answer support" : "Resolve source evidence"}
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {stale && (
            <Alert severity="warning">
              The answer or case changed. Close this panel and reopen it to use
              the latest version.
            </Alert>
          )}
          <Box>
            <Typography variant="subtitle2">Complete patient answer</Typography>
            <Typography sx={{ whiteSpace: "pre-wrap" }}>
              {highlightedText(
                opened.answerText,
                answerHighlights(opened.answerText, opened.claims),
              )}
            </Typography>
          </Box>
          {reassessment && (
            <>
              {opened.failureReason && (
                <Alert severity="warning">{opened.failureReason}</Alert>
              )}
              <TextField
                label="Why is this answer supported?"
                helperText="Explain how the case supports the highlighted words. Your explanation will be checked against the evidence."
                placeholder="For example: these two listed items are inhalers."
                value={explanation}
                onChange={(e) => setExplanation(e.target.value)}
                multiline
                minRows={2}
                inputProps={{ maxLength: 2000 }}
                disabled={busy || stale}
                autoFocus
                fullWidth
              />
            </>
          )}
          <Typography variant="body2">
            {reassessment
              ? "The attached evidence will be reused. You can select additional or replacement passages below."
              : "Select the passages that support this answer, then recheck. You can select more than one."}
          </Typography>
          <Box component={reassessment ? "details" : "div"}>
            {reassessment && (
              <Box component="summary" sx={{ cursor: "pointer", mb: 1 }}>
                Choose supporting passages (optional)
              </Box>
            )}
            <Stack spacing={2}>
              <Box>
                <Typography
                  component="label"
                  htmlFor={searchId}
                  variant="subtitle2"
                  sx={{ display: "block", mb: 1 }}
                >
                  Search case text and values
                </Typography>
                <OutlinedInput
                  id={searchId}
                  type="search"
                  placeholder="For example: days ago"
                  size="small"
                  fullWidth
                  notched={false}
                  autoFocus={!reassessment}
                  value={search}
                  onChange={(e) => {
                    setSearch(e.target.value);
                    setPage(0);
                  }}
                />
              </Box>
              <Box>
                <Typography variant="subtitle2">Supporting passages</Typography>
                <Typography variant="caption" color="text.secondary">
                  {query || browseAll
                    ? matches.length
                      ? `${currentPage * PAGE_SIZE + 1}–${currentPage * PAGE_SIZE + visible.length} of ${matches.length}`
                      : "No matches"
                    : `${matches.length} suggested ${matches.length === 1 ? "passage" : "passages"}`}{" "}
                  · {selected.length} selected
                </Typography>
                <Stack spacing={1} sx={{ mt: 1 }}>
                  {visible.map((entry) => (
                    <FormControlLabel
                      key={entry.id}
                      sx={{
                        m: 0,
                        p: 1,
                        border: 1,
                        borderColor: selected.includes(entry.id)
                          ? "primary.main"
                          : "divider",
                        borderRadius: 1,
                        alignItems: "flex-start",
                      }}
                      control={
                        <Checkbox
                          checked={selected.includes(entry.id)}
                          disabled={
                            busy ||
                            stale ||
                            (selected.length >= 80 &&
                              !selected.includes(entry.id))
                          }
                          onChange={(_, checked) => toggle(entry.id, checked)}
                        />
                      }
                      label={
                        <Box
                          sx={{
                            pt: 0.75,
                            minWidth: 0,
                            overflowWrap: "anywhere",
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {entry.path
                              ? `Case value: ${fieldLabel(entry.path)}`
                              : "Case text"}
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: "pre-wrap" }}
                          >
                            {entry.quote}
                          </Typography>
                        </Box>
                      }
                    />
                  ))}
                  {!matches.length && (
                    <Typography variant="body2">
                      {query
                        ? "No passages match. Try a shorter phrase or clear the search."
                        : "Search the case or browse all passages to choose supporting evidence."}
                    </Typography>
                  )}
                </Stack>
                {!query && (
                  <Button
                    variant="outlined"
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => {
                      setBrowseAll((value) => !value);
                      setPage(0);
                    }}
                  >
                    {browseAll
                      ? "Show suggested passages"
                      : "Browse all passages"}
                  </Button>
                )}
                {pageCount > 1 && (
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{ mt: 1 }}
                  >
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={currentPage === 0}
                      onClick={() => setPage(currentPage - 1)}
                    >
                      Previous
                    </Button>
                    <Typography variant="caption">
                      Page {currentPage + 1} of {pageCount}
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      disabled={currentPage + 1 >= pageCount}
                      onClick={() => setPage(currentPage + 1)}
                    >
                      Next
                    </Button>
                  </Stack>
                )}
              </Box>
              {!!selectedEntries.length && (
                <Box component="details">
                  <Box component="summary" sx={{ cursor: "pointer" }}>
                    Selected evidence ({selectedEntries.length})
                  </Box>
                  {selectedEntries.map((entry) => (
                    <Box
                      key={entry.id}
                      sx={{
                        display: "flex",
                        gap: 1,
                        alignItems: "flex-start",
                        mt: 1,
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          flex: 1,
                          minWidth: 0,
                          whiteSpace: "pre-wrap",
                          overflowWrap: "anywhere",
                        }}
                      >
                        {entry.path ? `${fieldLabel(entry.path)}: ` : ""}
                        {entry.quote}
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={busy || stale}
                        aria-label={`Remove passage: ${entry.quote}`}
                        onClick={() => toggle(entry.id, false)}
                      >
                        Remove
                      </Button>
                    </Box>
                  ))}
                </Box>
              )}
            </Stack>
          </Box>
          <Box component="details" open={reassessment || undefined}>
            <Box component="summary" sx={{ cursor: "pointer" }}>
              Currently attached evidence
            </Box>
            {opened.evidence.length ? (
              opened.evidence.map((e, n) => (
                <Typography
                  key={n}
                  variant="body2"
                  sx={{
                    whiteSpace: "pre-wrap",
                    overflowWrap: "anywhere",
                    mt: 1,
                  }}
                >
                  {e.path ? `${fieldLabel(e.path)}: ` : ""}
                  {e.quote}
                </Typography>
              ))
            ) : (
              <Typography variant="body2">No evidence is attached.</Typography>
            )}
          </Box>
          <Box
            component="details"
            onToggle={(e) => setShowRaw(e.currentTarget.open)}
          >
            <Box component="summary" sx={{ cursor: "pointer" }}>
              View original case text
            </Box>
            {showRaw && (
              <Typography
                component="div"
                variant="body2"
                aria-label="Original case text"
                sx={{ mt: 1, whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
              >
                {opened.source.rawText
                  ? highlightedText(opened.source.rawText, sourceHighlights)
                  : "No original case text is available."}
              </Typography>
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            {sourceKind === "session"
              ? "Passing checks still requires administrator approval."
              : "Passing predicted answers can become available automatically."}{" "}
            Published answers stay available during checking.
          </Typography>
          {selected.length > 80 && (
            <Alert severity="warning">Select up to 80 passages.</Alert>
          )}
          {(error || reviewError) && (
            <Alert severity="error">{reviewError || error}</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        <Button variant="outlined" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={
            busy ||
            stale ||
            selected.length > 80 ||
            (reassessment
              ? !explanation.trim() ||
                (!selected.length &&
                  (!!opened.initialIds.length || !opened.evidence.length))
              : !selected.length)
          }
          onClick={async () => {
            if (busy || stale || submitting.current) return;
            submitting.current = true;
            setError("");
            try {
              const success = await onUse(
                selectedEntries.map(({ quote, path, location }) => ({
                  quote,
                  path,
                  location,
                })),
                reassessment ? explanation.trim() : undefined,
              );
              if (success) onClose();
              else
                setError(
                  "Evidence could not be saved. Review the error and try again.",
                );
            } catch (e) {
              setError(
                e instanceof Error ? e.message : "Evidence could not be saved.",
              );
            } finally {
              submitting.current = false;
            }
          }}
        >
          {busy
            ? "Saving…"
            : reassessment
              ? "Recheck with this explanation"
              : "Use this passage and recheck"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
